/**
 * RSA Encryption Utilities using Web Crypto API
 * No third-party dependencies required
 */

export interface RSAKeyPair {
  publicKey: string;
  privateKey: string;
}

export interface EncryptedPrivateKey {
  encryptedKey: string;
  iv: string;
  salt: string;
  authTag?: string; // Optional for backward compatibility
}

export const generateRSAKeyPair = async (): Promise<CryptoKeyPair> => {
  try {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true,
      ['encrypt', 'decrypt']
    );
    return keyPair;
  } catch (error) {
    console.error('RSA key pair generation failed:', error);
    throw new Error('Failed to generate RSA key pair');
  }
};

export const exportPublicKey = async (publicKey: CryptoKey): Promise<string> => {
  try {
    const exported = await crypto.subtle.exportKey('spki', publicKey);
    const exportedAsString = ab2str(exported);
    const exportedAsBase64 = btoa(exportedAsString);
    const pemKey = `-----BEGIN PUBLIC KEY-----\n${exportedAsBase64}\n-----END PUBLIC KEY-----`;
    return pemKey;
  } catch (error) {
    console.error('Public key export failed:', error);
    throw new Error('Failed to export public key');
  }
};

export const exportPrivateKey = async (privateKey: CryptoKey): Promise<ArrayBuffer> => {
  try {
    const exported = await crypto.subtle.exportKey('pkcs8', privateKey);
    
    // Convert to PEM format for consistent encryption/decryption
    const exportedAsString = ab2str(exported);
    const exportedAsBase64 = btoa(exportedAsString);
    const pemKey = `-----BEGIN PRIVATE KEY-----\n${exportedAsBase64}\n-----END PRIVATE KEY-----`;
    
    // Return as ArrayBuffer (encoded as UTF-8 text)
    const encoder = new TextEncoder();
    return encoder.encode(pemKey).buffer;
  } catch (error) {
    console.error('Private key export failed:', error);
    throw new Error('Failed to export private key');
  }
};

export const importPublicKey = async (pemKey: string): Promise<CryptoKey> => {
  try {
    const pemContents = pemKey
      .replace('-----BEGIN PUBLIC KEY-----', '')
      .replace('-----END PUBLIC KEY-----', '')
      .replace(/\n/g, '');
    const binaryDer = str2ab(atob(pemContents));
    const publicKey = await crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['encrypt']
    );
    return publicKey;
  } catch (error) {
    console.error('Public key import failed:', error);
    throw new Error('Failed to import public key');
  }
};

export const importPrivateKey = async (keyData: ArrayBuffer): Promise<CryptoKey> => {
  try {
    // Convert ArrayBuffer to string to check if it's PEM format
    const decoder = new TextDecoder();
    const keyString = decoder.decode(keyData);
    
    let binaryKey: ArrayBuffer;
    
    if (keyString.includes('-----BEGIN PRIVATE KEY-----')) {
      // PEM format - convert to binary
      const pemContents = keyString
        .replace('-----BEGIN PRIVATE KEY-----', '')
        .replace('-----END PRIVATE KEY-----', '')
        .replace(/\n/g, '')
        .trim();
      binaryKey = str2ab(atob(pemContents));
    } else {
      // Already binary PKCS8 format
      binaryKey = keyData;
    }
    
    const privateKey = await crypto.subtle.importKey(
      'pkcs8',
      binaryKey,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['decrypt']
    );
    return privateKey;
  } catch (error) {
    console.error('Private key import failed:', error);
    throw new Error('Failed to import private key');
  }
};

export const encryptPrivateKey = async (
  privateKeyData: ArrayBuffer,
  encryptionKey: string
): Promise<EncryptedPrivateKey> => {
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(encryptionKey),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );
    const aesKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    );
    const encryptedDataWithTag = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128, // 128 bits = 16 bytes
      },
      aesKey,
      privateKeyData
    );
    
    // Web Crypto API AES-GCM automatically includes the authTag as the last 16 bytes
    // We need to separate it to match backend format (Node.js crypto returns them separately)
    const encryptedDataWithTagArray = new Uint8Array(encryptedDataWithTag);
    const encryptedData = encryptedDataWithTagArray.slice(0, encryptedDataWithTagArray.length - 16);
    const authTag = encryptedDataWithTagArray.slice(encryptedDataWithTagArray.length - 16);
    
    return {
      encryptedKey: btoa(ab2str(encryptedData.buffer)),
      iv: btoa(ab2str(iv.buffer)),
      salt: btoa(ab2str(salt.buffer)),
      authTag: btoa(ab2str(authTag.buffer)),
    };
  } catch (error) {
    console.error('Private key encryption failed:', error);
    throw new Error('Failed to encrypt private key');
  }
};

export const decryptPrivateKey = async (
  encryptedPrivateKey: EncryptedPrivateKey,
  encryptionKey: string
): Promise<ArrayBuffer> => {
  try {
    let encryptedData: ArrayBuffer;
    
    // Handle both formats: with separate authTag (backend) and without (frontend)
    if (encryptedPrivateKey.authTag) {
      // Backend format (Node.js crypto): authTag is separate
      // In Node.js crypto, authTag is separate, but Web Crypto API expects it appended to ciphertext
      const dataBuffer = str2ab(atob(encryptedPrivateKey.encryptedKey));
      const authTagBuffer = str2ab(atob(encryptedPrivateKey.authTag));
      
      // For Web Crypto API AES-GCM, the auth tag must be appended to the ciphertext
      const combined = new Uint8Array(dataBuffer.byteLength + authTagBuffer.byteLength);
      combined.set(new Uint8Array(dataBuffer), 0);
      combined.set(new Uint8Array(authTagBuffer), dataBuffer.byteLength);
      encryptedData = combined.buffer;
    } else {
      // Frontend format: authTag is already included in encrypted data
      encryptedData = str2ab(atob(encryptedPrivateKey.encryptedKey));
    }
    
    const iv = str2ab(atob(encryptedPrivateKey.iv));
    const salt = str2ab(atob(encryptedPrivateKey.salt));
    
    // Derive the AES key from the encryption key using PBKDF2
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(encryptionKey),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );
    
    const aesKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );
    
    // Decrypt using AES-GCM
    // Note: Web Crypto API expects the auth tag to be part of the encrypted data (appended)
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128, // 128 bits = 16 bytes (standard for AES-GCM)
      },
      aesKey,
      encryptedData
    );
    
    return decryptedData;
  } catch (error) {
    console.error('Private key decryption failed:', error);
    console.error('Debug info:', {
      hasAuthTag: !!encryptedPrivateKey.authTag,
      ivLength: encryptedPrivateKey.iv?.length,
      saltLength: encryptedPrivateKey.salt?.length,
      encryptedKeyLength: encryptedPrivateKey.encryptedKey?.length,
    });
    throw new Error('Failed to decrypt private key');
  }
};

export const rsaEncrypt = async (data: string, publicKey: CryptoKey): Promise<string> => {
  try {
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    if (encodedData.length > 190) {
      throw new Error('Data too large for RSA encryption. Use hybrid encryption for large data.');
    }
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey,
      encodedData
    );
    return btoa(ab2str(encryptedData));
  } catch (error) {
    console.error('RSA encryption failed:', error);
    throw new Error('Failed to encrypt data with RSA');
  }
};

export const rsaDecrypt = async (encryptedData: string, privateKey: CryptoKey): Promise<string> => {
  try {
    const encryptedBuffer = str2ab(atob(encryptedData));
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'RSA-OAEP',
      },
      privateKey,
      encryptedBuffer
    );
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('RSA decryption failed:', error);
    throw new Error('Failed to decrypt data with RSA');
  }
};

export const hybridEncrypt = async (
  data: string,
  publicKey: CryptoKey
): Promise<{ encryptedData: string; encryptedKey: string; iv: string; authTag: string }> => {
  try {
    const aesKey = await crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt']
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    const encryptedDataWithTag = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      aesKey,
      encodedData
    );
    
    // AES-GCM appends the 16-byte auth tag to the end of the encrypted data
    // Split the encrypted data and auth tag
    const encryptedDataArray = new Uint8Array(encryptedDataWithTag);
    const encryptedData = encryptedDataArray.slice(0, encryptedDataArray.length - 16);
    const authTag = encryptedDataArray.slice(encryptedDataArray.length - 16);
    
    const exportedAesKey = await crypto.subtle.exportKey('raw', aesKey);
    const encryptedAesKey = await crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey,
      exportedAesKey
    );
    return {
      encryptedData: btoa(ab2str(encryptedData.buffer)),
      encryptedKey: btoa(ab2str(encryptedAesKey)),
      iv: btoa(ab2str(iv.buffer)),
      authTag: btoa(ab2str(authTag.buffer)),
    };
  } catch (error) {
    console.error('Hybrid encryption failed:', error);
    throw new Error('Failed to perform hybrid encryption');
  }
};

export const hybridDecrypt = async (
  encryptedData: string,
  encryptedKey: string,
  iv: string,
  privateKey: CryptoKey,
  authTag?: string
): Promise<string> => {
  try {
    console.log('=== HYBRID DECRYPT DEBUG ===');
    console.log('encryptedData type:', typeof encryptedData);
    console.log('encryptedData length:', encryptedData?.length);
    console.log('encryptedData sample:', encryptedData?.substring(0, 50));
    console.log('encryptedKey type:', typeof encryptedKey);
    console.log('encryptedKey length:', encryptedKey?.length);
    console.log('encryptedKey sample:', encryptedKey?.substring(0, 50));
    console.log('iv type:', typeof iv);
    console.log('iv value:', iv);
    console.log('authTag type:', typeof authTag);
    console.log('authTag value:', authTag);
    console.log('============================');
    
    // Decrypt the RSA-encrypted AES key
    const encryptedAesKeyBuffer = str2ab(atob(encryptedKey));
    const aesKeyBuffer = await crypto.subtle.decrypt(
      {
        name: 'RSA-OAEP',
      },
      privateKey,
      encryptedAesKeyBuffer
    );
    
    // Import the AES key
    const aesKey = await crypto.subtle.importKey(
      'raw',
      aesKeyBuffer,
      {
        name: 'AES-GCM',
        length: 256,
      },
      false,
      ['decrypt']
    );
    
    const ivBuffer = str2ab(atob(iv));
    
    // For AES-GCM, the auth tag must be appended to the ciphertext
    // Backend sends them separately, so we need to concatenate
    let encryptedDataBuffer: ArrayBuffer;
    if (authTag) {
      const dataBuffer = str2ab(atob(encryptedData));
      const authTagBuffer = str2ab(atob(authTag));
      
      // Concatenate encrypted data and auth tag
      const combined = new Uint8Array(dataBuffer.byteLength + authTagBuffer.byteLength);
      combined.set(new Uint8Array(dataBuffer), 0);
      combined.set(new Uint8Array(authTagBuffer), dataBuffer.byteLength);
      encryptedDataBuffer = combined.buffer;
    } else {
      // Auth tag already included in encrypted data
      encryptedDataBuffer = str2ab(atob(encryptedData));
    }
    
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBuffer,
      },
      aesKey,
      encryptedDataBuffer
    );
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('Hybrid decryption failed:', error);
    throw new Error('Failed to perform hybrid decryption');
  }
};

const str2ab = (str: string): ArrayBuffer => {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0; i < str.length; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
};

const ab2str = (buf: ArrayBuffer): string => {
  return String.fromCharCode(...Array.from(new Uint8Array(buf)));
};

export const isValidPublicKey = (pemKey: string): boolean => {
  return (
    pemKey.includes('-----BEGIN PUBLIC KEY-----') &&
    pemKey.includes('-----END PUBLIC KEY-----')
  );
};

export const generateAndExportKeyPair = async (
  encryptionKey: string
): Promise<{ publicKey: string; encryptedPrivateKey: EncryptedPrivateKey }> => {
  try {
    const keyPair = await generateRSAKeyPair();
    const publicKeyPem = await exportPublicKey(keyPair.publicKey);
    const privateKeyData = await exportPrivateKey(keyPair.privateKey);
    const encryptedPrivateKey = await encryptPrivateKey(privateKeyData, encryptionKey);
    return {
      publicKey: publicKeyPem,
      encryptedPrivateKey,
    };
  } catch (error) {
    console.error('Key pair generation and export failed:', error);
    throw new Error('Failed to generate and export RSA key pair');
  }
};

export const loadPrivateKey = async (
  encryptedPrivateKey: EncryptedPrivateKey,
  encryptionKey: string
): Promise<CryptoKey> => {
  try {
    // Decrypt the private key (returns ArrayBuffer containing PEM text)
    const privateKeyData = await decryptPrivateKey(encryptedPrivateKey, encryptionKey);
    
    // Convert ArrayBuffer to string (PEM format)
    const decoder = new TextDecoder();
    const privateKeyPem = decoder.decode(privateKeyData);
    
    // Convert PEM to binary PKCS8 format
    // Remove PEM headers and decode base64
    const pemHeader = '-----BEGIN PRIVATE KEY-----';
    const pemFooter = '-----END PRIVATE KEY-----';
    const pemContents = privateKeyPem
      .replace(pemHeader, '')
      .replace(pemFooter, '')
      .replace(/\s/g, ''); // Remove whitespace and newlines
    
    // Decode base64 to binary
    const binaryDer = str2ab(atob(pemContents));
    
    // Import the binary PKCS8 key
    const privateKey = await importPrivateKey(binaryDer);
    return privateKey;
  } catch (error) {
    console.error('Private key loading failed:', error);
    throw new Error('Failed to load private key');
  }
};
